import { collection, getDocs, query, where } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { eventAdminStateSchema } from "./schemas";

export type EventAdminsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; error: string }
  // `unreadable` counts documents that failed the schema. They are not
  // dropped silently: the list says how many it could not read, because a
  // missing admin and an unreadable row look the same on screen otherwise.
  | { status: "success"; uids: string[]; unreadable: number };

type Settled = { eventId: string } & Exclude<
  EventAdminsState,
  { status: "idle" } | { status: "loading" }
>;

/**
 * The uids of the current administrators of one event, for a super admin.
 *
 * Reads `event_admin_states`, the trigger-owned mirror of SET_EVENT_ADMIN,
 * because `events/{id}/members` lists only for an admin of that event and a
 * super admin is not admin of every event. `firestore.rules` lets a super admin
 * `get` and `list` it and nobody write it.
 *
 * The query is a single equality on `eventId`, which Firestore serves from its
 * automatic single-field index. `isEventAdmin` is filtered here rather than in
 * the query so no composite index is needed: removing an admin leaves a state
 * document with `isEventAdmin: false`, so the collection holds both.
 *
 * Names and emails are not read here. The caller resolves the uids against the
 * users it has already loaded, so there is no read per admin.
 *
 * A reload keeps the previous list on screen until the new one arrives; only
 * changing the event shows the loading state.
 */
export function useEventAdmins(eventId: string): EventAdminsState & {
  reload: () => void;
} {
  const [settled, setSettled] = useState<Settled | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;

    getDocs(
      query(
        collection(db, "event_admin_states"),
        where("eventId", "==", eventId),
      ),
    )
      .then((snap) => {
        if (cancelled) return;
        const uids: string[] = [];
        let unreadable = 0;
        snap.forEach((d) => {
          const result = eventAdminStateSchema.safeParse(d.data());
          if (!result.success) unreadable += 1;
          else if (result.data.isEventAdmin === true)
            uids.push(result.data.uid);
        });
        setSettled({ eventId, status: "success", uids, unreadable });
      })
      .catch((err) => {
        if (cancelled) return;
        setSettled({
          eventId,
          status: "error",
          error:
            err instanceof Error ? err.message : "Failed to load event admins",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  // A result for another event is stale, so it reads as loading.
  const state: EventAdminsState = !eventId
    ? { status: "idle" }
    : settled && settled.eventId === eventId
      ? settled
      : { status: "loading" };

  return { ...state, reload };
}
